import { type JSX, useEffect, useMemo } from 'react';
import { useParams } from 'react-router';
import { ErrorPage } from '../../components/ErrorPage/ErrorPage.tsx';
import { PageHeader } from '../../components/PageHeader/PageHeader.tsx';
import type { DealLibraryItem } from '../../types/index.ts';
import { LoadingSkeleton } from './components/LoadingSkeleton.tsx';
import { DealDetailsSection } from './sections/DealDetailsSection.tsx';
import { TargetingCriteriaSection } from './sections/TargetingCriteriaSection.tsx';

type DetailsProps = {
  dealTemplates: DealLibraryItem[];
  isLoading: boolean;
  searchParams: string;
};

export const Details = ({ dealTemplates, isLoading, searchParams }: DetailsProps): JSX.Element => {
  const params = useParams();
  const deal = useMemo(
    () => dealTemplates.find(dealTemplate => dealTemplate.id === params.id),
    [dealTemplates, params.id]
  );

  /**
   * Scroll to the top of the page when the component mounts.
   * This is essential when dealing with a larger list of dynamically loaded targeting elements,
   * ensuring a consistent and optimal user experience by presenting the page starting from the top.
   */
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  if (isLoading)
    return (
      <>
        <PageHeader isDetailsPage isLoading />
        <LoadingSkeleton />
      </>
    );

  if (!deal) return <ErrorPage errorType={404} />;

  return (
    <>
      <PageHeader isDetailsPage title={deal.name} searchParams={searchParams} />
      <DealDetailsSection deal={deal} />
      <TargetingCriteriaSection deal={deal} />
    </>
  );
};
